import Alpine from 'alpinejs'
import intersect from '@alpinejs/intersect'
import Swiper from 'swiper';
import { Navigation, Pagination } from 'swiper/modules';
import 'swiper/css';
import './main.css'

// Active HMR
if (import.meta.hot) {
  import.meta.hot.accept(() => {
    console.log("HMR")
  });
}

// ---------------------------------------------------------------------------------------------------- ALPINE AND THEME SWITCHING
// Cookie setter and getter (Alpine needs access to this, so it has to be up top)
function setCookie(cname, cvalue, exdays) {
  const d = new Date();
  d.setTime(d.getTime() + (exdays*24*60*60*1000));
  let expires = 'expires='+ d.toUTCString();
  document.cookie = cname + '=' + cvalue + ';' + expires + ';path=/';
}
function getCookie(cname) {
  let name = cname + '=';
  let decodedCookie = decodeURIComponent(document.cookie);
  let ca = decodedCookie.split(';');
  for(let i = 0; i <ca.length; i++) {
    let c = ca[i];
    while (c.charAt(0) == ' ') {
      c = c.substring(1);
    }
    if (c.indexOf(name) == 0) {
      return c.substring(name.length, c.length);
    }
  }
  return '';
}
window.setCookie = setCookie
window.getCookie = getCookie

// Alpine setup
window.Alpine = Alpine
Alpine.plugin(intersect)

// Alpine shared data
document.addEventListener('alpine:init', () => {
  Alpine.data('theme', () => ({
    setTheme(theme) { 
      let r = document.querySelector(':root'); 
      if (theme == 'dark') {
        r.style.setProperty('--color-bg', 'var(--color-midnight)' ); 
        r.style.setProperty('--color-base', 'var(--color-beige)' ); 
        r.style.setProperty('--color-accent', 'var(--color-neon)' ); 
      } else {
        r.style.setProperty('--color-bg', 'var(--color-beige)' ); 
        r.style.setProperty('--color-base', 'var(--color-midnight)' ); 
        r.style.setProperty('--color-accent', 'var(--color-flame)' ); 
      }
    }
  }))
})

// Alpine init
Alpine.start();

/* REFERENCES 
https://github.com/croxton/craftcms-hda-starter-kit
https://github.com/madebyshape/craft-cms/tree/master
*/

// ---------------------------------------------------------------------------------------------------- HELPER FUNCTIONS
// Throttle implementation taken from Underscore: https://underscorejs.org/docs/modules/throttle.html
function now() {
  return new Date().getTime();
}

function throttle(func, wait, options) {
  var timeout, context, args, result;
  var previous = 0;
  if (!options) options = {};

  var later = function() {
    previous = options.leading === false ? 0 : now();
    timeout = null;
    result = func.apply(context, args);
    if (!timeout) context = args = null;
  };

  var throttled = function() {
    var _now = now();
    if (!previous && options.leading === false) previous = _now;
    var remaining = wait - (_now - previous);
    context = this;
    args = arguments;
    if (remaining <= 0 || remaining > wait) {
      if (timeout) {
        clearTimeout(timeout);
        timeout = null;
      }
      previous = _now;
      result = func.apply(context, args);
      if (!timeout) context = args = null;
    } else if (!timeout && options.trailing !== false) {
      timeout = setTimeout(later, remaining);
    }
    return result;
  };

  throttled.cancel = function() {
    clearTimeout(timeout);
    previous = 0;
    timeout = context = args = null;
  };

  return throttled;
}

// ---------------------------------------------------------------------------------------------------- PARALLAX
function parallax(element) {
  let elementTop = window.scrollY;

  if (element.closest("[parallax-container]")) {
    elementTop = elementTop - element.closest("[parallax-container]").offsetTop + window.innerHeight / 2;
    if (elementTop < 0) elementTop = 0;
  }

  let elementPos = elementTop * parseFloat(element.getAttribute("parallax-speed") || 0);
  let windowHeight = window.innerHeight;
  let elementStart = -1 * window.innerHeight;

  if (element.hasAttribute("parallax-max")) {
    windowHeight = element.offsetHeight * parseFloat(element.getAttribute("parallax-max"));
    if (windowHeight < elementPos) elementPos = windowHeight;
  }

  if (element.hasAttribute("parallax-min")) {
    elementStart = element.offsetHeight * parseFloat(element.getAttribute("parallax-min")) * -1;
    if (elementPos < elementStart) elementPos = elementStart;
  }

  element.style.transform = `translateY(${elementPos}px)`;
}

function updateParallaxElements() {
  document.querySelectorAll("[parallax]").forEach((element) => {
    parallax(element);
  });
}

// On load, update the parallax, and then update it at max every 50ms
updateParallaxElements();
const throttledUpdateParallaxElements = throttle(updateParallaxElements, 50);
window.addEventListener("scroll", throttledUpdateParallaxElements);


// ---------------------------------------------------------------------------------------------------- SWIPER
const slideGap = 16;

function calculateEdgeOffset() {
  let containerExample = document.querySelector('.container');
  let containerPadding = parseInt(window.getComputedStyle(containerExample).paddingLeft);
  let containerWidth = containerExample.clientWidth - (2 * containerPadding);
  let edgeOffset = (document.getElementsByTagName('html')[0].clientWidth - containerWidth) / 2;
  return edgeOffset;
}

function reApplyEdgeOffsetToSwiper(swiperCarousel) {
  let edgeOffset = calculateEdgeOffset();
  swiperCarousel.params.breakpoints[900].slidesOffsetBefore = edgeOffset;
  swiperCarousel.params.breakpoints[900].slidesOffsetAfter = edgeOffset  - (slideGap * 2);
  swiperCarousel.params.breakpoints[1680].slidesOffsetBefore = edgeOffset;
  swiperCarousel.params.breakpoints[1680].slidesOffsetAfter = edgeOffset  - (slideGap * 2);
  swiperCarousel.currentBreakpoint = false; // hack to force swiper to reevaluate breakpoints
  swiperCarousel.update();
}

const swiperCarousels = document.querySelectorAll('.swiper');
for (let i = 0; i < swiperCarousels.length; i++) {

  swiperCarousels[i].closest('section').classList.add('swiper-section-'+i);
  const isOffset = (swiperCarousels[i].classList.contains('swiper-offset'));
  let edgeOffset = calculateEdgeOffset();
  let breakpoints = {};
  if (isOffset) {
    breakpoints = {
      900: {
        slidesOffsetBefore: edgeOffset,
        slidesOffsetAfter: edgeOffset - (slideGap * 2),
        slidesPerGroup: 3,
      },
      1680: {
        slidesOffsetBefore: edgeOffset,
        slidesOffsetAfter: edgeOffset - (slideGap * 2),
        slidesPerGroup: 4,
      },
    };
  } else {
    breakpoints = {
      900: {
        slidesPerGroup: 3,
      },
      1680: {
        slidesPerGroup: 4,
      },
    };
  }

  // Initialize carousel
  const swiperCarousel = new Swiper('.swiper-section-'+i+' .swiper', {
    modules: [Navigation, Pagination],
    slidesPerView: isOffset ? 'auto' : 1,
    slidesPerGroup: 1,
    spaceBetween: slideGap,
    autoHeight: false,
    updateOnWindowResize: true,
    navigation: {
      nextEl: '.swiper-section-'+i+' .swiper-button-next',
      prevEl: '.swiper-section-'+i+' .swiper-button-prev',
    },
    pagination: {
      el: '.swiper-section-'+i+' .swiper-pagination',
      clickable: true
    },
    breakpoints: breakpoints
  });

  // Offset carousels need to recalculate edges on resize
  if (isOffset) {
    swiperCarousel.on('resize', function() {
      reApplyEdgeOffsetToSwiper(swiperCarousel);
    });

    // This is only necessary because of Vite's FOUC
    window.setTimeout(function() {
      reApplyEdgeOffsetToSwiper(swiperCarousel);
    },500);
  }
}

//---------------------------------------------------------------------------[ BREADCRUMB/TAB SCROLLER ]
const scrollableElements = document.querySelectorAll('.js-scroller');

function adjustScrollableElement(el) {
  const buttonRight = el.nextElementSibling && el.nextElementSibling.classList.contains('js-scroller-right-btn') ? el.nextElementSibling : null;
  const buttonLeft = el.previousElementSibling && el.previousElementSibling.classList.contains('js-scroller-left-btn') ? el.previousElementSibling : null;

  const scrollList = el.querySelector('.js-scroller-list');
  if (!scrollList) return;

  const listWidth = scrollList.offsetWidth;
  const visibleWidth = el.offsetWidth;
  const distanceToEnd = listWidth - visibleWidth - el.scrollLeft;

  // Start of list with hidden area to right
  if (el.scrollLeft == 0 && listWidth > visibleWidth) {
    el.classList.remove('mask-fade-left', 'mask-fade-both');
    el.classList.add('mask-fade-right');
    if (buttonRight) buttonRight.classList.remove('opacity-0', 'pointer-events-none');
    if (buttonLeft) buttonLeft.classList.add('opacity-0', 'pointer-events-none');
  
  // Whole list is visible, no need for anything
  } else if (visibleWidth >= listWidth) {
    el.classList.remove('mask-fade-left', 'mask-fade-both', 'mask-fade-right');
    if (buttonRight) buttonRight.classList.add('opacity-0', 'pointer-events-none');
    if (buttonLeft) buttonLeft.classList.add('opacity-0', 'pointer-events-none');
  
  // End of list with hidden area to left
  } else if (distanceToEnd < 1) {
    el.classList.remove('mask-fade-right', 'mask-fade-both');
    el.classList.add('mask-fade-left');
    if (buttonRight) buttonRight.classList.add('opacity-0', 'pointer-events-none');
    if (buttonLeft) buttonLeft.classList.remove('opacity-0', 'pointer-events-none');

  // Middle of list with hidden area to both sides
  } else if (el.scrollLeft > 0) {
    el.classList.remove('mask-fade-right', 'mask-fade-left');
    el.classList.add('mask-fade-both');
    if (buttonRight) buttonRight.classList.remove('opacity-0', 'pointer-events-none');
    if (buttonLeft) buttonLeft.classList.remove('opacity-0', 'pointer-events-none');
  }
}

function scrollScrollableElement(direction, scroller) {
  if (!scroller) return;
  scroller.scrollBy({ 
    left: (direction == 'left' ? -0.5 * scroller.offsetWidth : 0.5 * scroller.offsetWidth), 
    behavior: 'smooth'
  })
}

function initScrollableElements() {
  // Add scroll listeners
  scrollableElements.forEach(el => {
    el.addEventListener('scroll', () => adjustScrollableElement(el));

    window.setTimeout(() => {
      adjustScrollableElement(el);
    }, 500);
  });
  window.addEventListener('resize', () => {
    scrollableElements.forEach(el => adjustScrollableElement(el));
  });

  // Buttons
  document.querySelectorAll('.js-scroller-left-btn, .js-scroller-right-btn').forEach(btn => {
    const direction = btn.classList.contains('js-scroller-left-btn') ? 'left' : 'right';

    const scroller = direction == 'left' ? 
      (btn.nextElementSibling && btn.nextElementSibling.classList.contains('js-scroller') ? btn.nextElementSibling : null) :
      (btn.previousElementSibling && btn.previousElementSibling.classList.contains('js-scroller') ? btn.previousElementSibling : null);
    
    btn.addEventListener('click', e => { e.preventDefault(); scrollScrollableElement(direction, scroller); });
    btn.addEventListener('touchstart', e => { e.preventDefault(); scrollScrollableElement(direction, scroller); });
  });
}

// Init if any scrollable elements on page
if (scrollableElements.length) {
  initScrollableElements();
}

// ---------------------------------------------------------------------------------------------------- MODALS
const modalButtons = document.querySelectorAll('button[data-modal]');

for (let i = 0; i < modalButtons.length; i++) {
  modalButtons[i].addEventListener('click', () => {
    const targetModal = modalButtons[i].getAttribute('data-modal');
    document.getElementById(targetModal).showModal();
  });
}